/**
 * Parse a delivery item from the API format to the app's internal format
 * Handles both "order" (restaurant) and "package" (document/goods) types
 */

function parseOrderType(item) {
  const content = item.content || item;
  
  // Dans la v3, pricing est à la racine, et contient deliveryFee, total, riderPayout, etc.
  const deliveryFee = content.pricing?.deliveryFee || content.deliveryFee || 0;
  
  // Rémunération / Commission nette du livreur (riderPayout)
  const riderCommission = content.pricing?.riderPayout ?? content.metadata?.riderFee ?? deliveryFee;

  // Détection du mode de paiement et de l'encaissement à la livraison (Cash)
  const paymentMethod = (content.payment?.method || content.paymentMethod || '').toLowerCase();
  const isCash = paymentMethod.includes('cash') || paymentMethod === 'cash_on_delivery' || paymentMethod === 'especes';
  const paymentPaid = content.payment?.status === 'success' || content.payment?.status === 'paid' || content.paymentStatus === 'success';
  const isCashOnDelivery = isCash && !paymentPaid;
  const paymentAmount = isCashOnDelivery ? (content.pricing?.total || content.totalAmount || deliveryFee) : null;

  // Extraire les timestamps depuis la timeline (format plat) ou timestamps (ancien format)
  const getTimestamp = (status) =>
    item.timeline?.find(t => t.status === status)?.timestamp || null

  const assignedAt =
    item.timestamps?.assignedAt || item.assignedAt || getTimestamp('accepted') || getTimestamp('assigned')

  // Séparation claire entre l'arrivée au pickup et la fin du pickup
  const pickupReachedAt =
    item.timestamps?.pickupReachedAt || item.pickupReachedAt || getTimestamp('pickup_reached') || getTimestamp('arrived_at_pickup')

  const pickedAt =
    item.timestamps?.pickedUpAt || item.pickedUpAt || item.pickedAt || getTimestamp('picked_up')

  const arrivedAt =
    item.timestamps?.arrivedAt || item.timestamps?.destinationReachedAt || item.arrivedAt || getTimestamp('arrived') 

  const deliveredAt =
    item.timestamps?.deliveredAt || item.deliveredAt || getTimestamp('delivered')

  // riderId : dans le format plat il est dans delivery.rider.riderId ou directement riderId
  const riderId =
    item.delivery?.rider?.riderId || item.riderId || null

  // pickupReached : déduit du statut, du timestamp d'arrivée ou d'un pickedAt existant
  const statusesPastPickup = ['picked_up', 'arrived', 'delivered', 'completed']
  const pickupReached =
    item.pickupReached ||
    !!pickupReachedAt ||
    !!pickedAt ||
    statusesPastPickup.includes(item.status)

  // destinationReached : lié au statut 'arrived' (ainsi qu'aux statuts ultérieurs)
  const statusesPastArrival = ['arrived', 'delivered', 'completed']
  const destinationReached =
    item.destinationReached ||
    !!item.destinationReachedAt ||
    !!arrivedAt ||
    statusesPastArrival.includes(item.status)

  return {
    ...item,
    id: item.id,
    status: item.status,
    itemStatus: content.status || item.status,
    // deliveryCode = DLVR-xxx (champ direct), sinon reference ORD-xxx
    deliveryCode: item.deliveryCode || item.reference || content.reference,
    orderDate: content.createdAt || item.createdAt || new Date().toISOString(),
    
    // Pickup Info (Restaurant)
    pickupLabel: content.restaurantName || 'Restaurant',
    pickupAddress: content.metadata?.restaurantLocation?.address || 
      (typeof content.pickupAddress === 'string' ? content.pickupAddress : null) ||
      [content.pickupAddress?.street, content.pickupAddress?.city].filter(Boolean).join(', ') || '',
    pickupLat: content.metadata?.restaurantLocation?.lat || content.pickupAddress?.coordinates?.latitude || content.pickupAddress?.latitude || null,
    pickupLng: content.metadata?.restaurantLocation?.lng || content.pickupAddress?.coordinates?.longitude || content.pickupAddress?.longitude || null,
    pickupCity: content.metadata?.restaurantLocation?.city || content.pickupAddress?.city || '',
    pickupInstructions: content.metadata?.restaurantLocation?.locationDescription || content.pickupAddress?.instructions || content.pickupAddressInstructions || '',

    // Destination Info (Client)
    destinationLabel: content.deliveryLocation?.isPickup ? 'Retrait au restaurant' : 'Livraison',
    destinationAddress: content.deliveryLocation?.isPickup
      ? (content.metadata?.restaurantLocation?.address || content.deliveryLocation?.address || '')
      : (content.deliveryLocation?.address || 
         (typeof content.deliveryAddress === 'string' ? content.deliveryAddress : null) ||
         [content.deliveryAddress?.street, content.deliveryAddress?.city].filter(Boolean).join(', ') || ''),
    destinationLat: content.deliveryLocation?.isPickup
      ? (content.metadata?.restaurantLocation?.lat || null)
      : ((content.deliveryLocation?.lat && content.deliveryLocation.lat !== 0 ? content.deliveryLocation.lat : null) ||
         (content.deliveryLocation?.latitude && content.deliveryLocation.latitude !== 0 ? content.deliveryLocation.latitude : null) ||
         content.deliveryAddress?.coordinates?.latitude || content.deliveryAddress?.latitude || null),
    destinationLng: content.deliveryLocation?.isPickup
      ? (content.metadata?.restaurantLocation?.lng || null)
      : ((content.deliveryLocation?.lng && content.deliveryLocation.lng !== 0 ? content.deliveryLocation.lng : null) ||
         (content.deliveryLocation?.longitude && content.deliveryLocation.longitude !== 0 ? content.deliveryLocation.longitude : null) ||
         content.deliveryAddress?.coordinates?.longitude || content.deliveryAddress?.longitude || null),
    destinationCity: content.deliveryLocation?.city || content.deliveryAddress?.city,
    destinationCountry: content.deliveryLocation?.country || content.deliveryAddress?.country || 'Bénin',
    destinationInstructions: content.deliveryLocation?.description || content.deliveryAddress?.instructions || content.customerNote,

    packageType: 'food',
    packageDescription: content.items
      ?.map(i => `${i.name || i.title || i.itemName} (x${i.quantity || 1})`)
      .join(', '),

    riderCommission: riderCommission,
    
    // Amount to pay (Show only if cash on delivery)
    paymentAmount: paymentAmount,
    paymentCurrency: content.payment?.currency || content.pricing?.currency || content.currency || 'XOF',
    paymentMode: isCash ? 'cash' : 'online',
    isCashOnDelivery: isCashOnDelivery,
    paymentPaid: paymentPaid,

    customerId: content.userId || content.customerId,
    customerName: content.deliveryLocation?.contactName || content.summary?.customerName || content.customerName || 'Client',
    customerPhoneNumber: content.deliveryLocation?.contactPhone || content.customerPhone || content.customerPhoneNumber,
    restaurantPhone: content.restaurantPhone || content.restaurantPhoneNumber,

    // Contenu de la commande
    items: content.items || [],
    drinks: content.drinks || content.metadata?.drinks || [],
    restaurantName: content.restaurantName||'Restaurant',
    orderReference: (item.reference || item.itemId?.split('-')[0])?.toUpperCase(),
    riderId,
    distanceKm: item.distanceKm || item.distance || content.pricing?.distance,
    estimatedDurationMin: item.durationMin || item.duration,

    type: "order",

    pickupReached,
    pickupReachedAt,
    destinationReached,
    assignedAt,
    pickedAt,
    destinationReachedAt: arrivedAt,
    deliveredAt,
  }
}

function parsePackageType(item) {
  const content = item.content || item;
  
  // Rémunération du livreur (pricing.riderPayout dans la v3)
  const deliveryFee = content.pricing?.deliveryFee || content.deliveryFee || 0;
  const riderCommission = content.pricing?.riderPayout ?? content.metadata?.riderFee ?? deliveryFee;

  // Détection du mode de paiement et de l'encaissement à la livraison (Cash)
  const paymentMethod = (content.payment?.method || content.paymentMethod || '').toLowerCase();
  const isCash = paymentMethod.includes('cash') || paymentMethod === 'cash_on_delivery' || paymentMethod === 'especes';
  const paymentPaid = content.payment?.status === 'success' || content.payment?.status === 'paid' || content.paymentStatus === 'success';
  const isCashOnDelivery = isCash && !paymentPaid;
  const paymentAmount = isCashOnDelivery ? (content.pricing?.total || content.price || content.paymentAmount || deliveryFee) : null;

  // Récupération de la liste brute des checkpoints
  const rawCheckpoints = content.checkpoints || content.route?.checkpoints || [];

  // Analyse de la timeline pour extraire le statut des checkpoints
  const timelineEvents = item.timeline || content.timeline || [];

  // Fallback : metadata.locationsFull contient les descriptions complètes
  // si l'API ne les a pas persisté directement dans pickup/destination/checkpoints
  const locationsFull = content.metadata?.locationsFull || {};
  const pickupFull = locationsFull.pickup || {};
  const destinationFull = locationsFull.destination || {};
  const checkpointsFull = locationsFull.checkpoints || [];

  const checkpoints = rawCheckpoints.map((checkpoint, i) => {
    // Événement d'arrivée au checkpoint
    const reachedEvent = timelineEvents.find(
      t => t.status === 'checkpoint_reached' && t.metadata?.checkpoint?.checkpointPosition === i
    );

    // Événement de livraison au checkpoint
    const deliveredEvent = timelineEvents.find(
      t => t.status === 'checkpoint_delivered' && t.metadata?.checkpoint?.checkpointPosition === i
    );

    // Description : champ direct de l'API, sinon fallback locationsFull
    const checkpointDescription =
      checkpoint.description ||
      checkpointsFull[i]?.description ||
      '';

    return {
      id: checkpoint.id || `checkpoint-${i}`,
      label: checkpoint.label || `Checkpoint ${i + 1}`,
      address: checkpoint.address || '',
      // Description de localisation du checkpoint (indications pour le rider)
      description: checkpointDescription,
      lat: checkpoint.lat || checkpoint.latitude,
      lng: checkpoint.lng || checkpoint.longitude,
      reached: !!reachedEvent || checkpoint.reached || false,
      reachedAt: reachedEvent?.timestamp || checkpoint.reachedAt || null,
      delivered: !!deliveredEvent || checkpoint.delivered || false,
      deliveredAt: deliveredEvent?.timestamp || checkpoint.deliveredAt || null,
      position: i,
      // Ingestion des infos client propres au checkpoint
      customerName: checkpoint.contactName || checkpoint.customerName || content.pickup?.contactName || 'Client',
      customerPhoneNumber: checkpoint.phone || checkpoint.contactPhone || checkpoint.customerPhone || content.pickup?.phone || '',
    };
  });

  const getTimestamp = (status) =>
    timelineEvents.find(t => t.status === status)?.timestamp || null;

  const assignedAt =
    item.timestamps?.assignedAt || item.acceptedAt || item.assignedAt || getTimestamp('assigned');

  const pickupReachedAt =
    item.timestamps?.pickupReachedAt || item.pickupReachedAt || getTimestamp('pickup_reached') || getTimestamp('arrived_at_pickup');

  const pickedAt =
    item.timestamps?.pickedUpAt || item.pickedUpAt || item.pickedAt || getTimestamp('picked_up');

  const arrivedAt =
    item.timestamps?.arrivedAt || item.timestamps?.destinationReachedAt || item.arrivedAt || getTimestamp('arrived') || getTimestamp('arrived_at_destination');

  const deliveredAt =
    item.timestamps?.deliveredAt || item.deliveredAt || getTimestamp('delivered');

  const pickupReached = item.pickupReached || !!pickupReachedAt || !!pickedAt;

  const statusesPastArrival = ['arrived', 'delivered', 'completed'];
  const destinationReached =
    item.destinationReached ||
    !!item.destinationReachedAt ||
    !!item.timestamps?.destinationReachedAt ||
    !!arrivedAt ||
    statusesPastArrival.includes(item.status);

  return {
    ...item,
    id: item.id,
    status: item.status,
    itemStatus: content.status || item.status,
    deliveryCode: content.deliveryCode || item.reference,
    orderDate: content.createdAt || new Date().toISOString(),

    // Pickup Info
    pickupLabel: content.pickupLabel || 'Ramassage',
    pickupAddress: content.pickup?.address || content.pickupAddress || '',
    pickupLat: content.pickup?.latitude || content.pickup?.lat || content.pickupLat,
    pickupLng: content.pickup?.longitude || content.pickup?.lng || content.pickupLng,
    pickupCity: content.pickupCity || 'Bénin',
    // Description : champ direct de l'API → fallback metadata.locationsFull.pickup
    pickupInstructions: content.pickup?.description || pickupFull.description || content.pickupDescription || content.notes || '',
    pickupCustomerName: content.pickup?.contactName || content.customerName || 'Client',
    pickupCustomerPhone: content.pickup?.phone || content.customerPhone || '',

    // Destination Info
    destinationLabel: content.destinationLabel || 'Destination',
    destinationAddress: content.destination?.address || content.destinationAddress || '',
    destinationLat: content.destination?.latitude || content.destination?.lat || content.destinationLat,
    destinationLng: content.destination?.longitude || content.destination?.lng || content.destinationLng,
    destinationCity: content.destinationCity || 'Bénin',
    // Description : champ direct de l'API → fallback metadata.locationsFull.destination
    destinationInstructions: content.destination?.description || destinationFull.description || content.destinationDescription || content.notes || '',
    destinationCustomerName: content.destination?.contactName || content.customerName || 'Client',
    destinationCustomerPhone: content.destination?.phone || content.customerPhone || '',

    checkpoints,
    packageType: content.packageInfo?.category || content.packageType || 'package',
    packageDescription: content.packageInfo?.description || content.description || content.pickupDescription || content.title,
    packagePhoto: content.photo || content.packagePhoto,

    riderCommission: riderCommission,
    
    // Amount to pay (Show only if cash on delivery)
    paymentAmount: paymentAmount,
    paymentCurrency: content.payment?.currency || content.pricing?.currency || content.currency || 'XOF',
    paymentMode: isCash ? 'cash' : 'online',
    isCashOnDelivery: isCashOnDelivery,
    paymentPaid: paymentPaid,

    distanceKm: item.distanceKm || item.distance || content.route?.totalDistance,
    estimatedDurationMin: item.durationMin || item.duration || content.route?.totalDuration,

    customerId: content.userId || content.customerId,
    customerName: content.pickup?.contactName || content.destination?.contactName || content.customerName || 'Client',
    customerPhoneNumber: content.pickup?.phone || content.destination?.phone || content.customerPhone || content.customerPhoneNumber,
    orderReference: (item.itemId?.split('-')[0] || item.reference)?.toUpperCase(),
    
    riderId: item.riderId || null,
    type: "package",

    pickupReached,
    pickupReachedAt,
    destinationReached,
    assignedAt,
    pickedAt,
    destinationReachedAt: arrivedAt,
    deliveredAt,
  }
}

/**
 * Main parse function - handles both API v3 new format and legacy format
 */
function parseExternalDelivery(item) {
  if (!item) return null

  let data = item

  // Detect type (new flat format)
  const isFlatPackage = item.packageInfo || item.reference?.startsWith('PKG-') || item.type === 'package';
  const isFlatOrder = item.items || item.restaurantName || item.reference?.startsWith('ORD-') || item.type === 'order';

  if (item.content && item.type) {
    // Legacy nested format
    if (item.type === 'order') {
      data = parseOrderType(item)
    } else if (item.type === 'package') {
      data = parsePackageType(item)
    }
  } else if (isFlatPackage) {
    data = parsePackageType(item)
  } else if (isFlatOrder) {
    data = parseOrderType(item)
  }

  // Final parsing wrapper to add extra computed properties
  return parseLegacyFormat(data)
}

/**
 * Construit la liste complète et ordonnée de toutes les étapes du trajet.
 * Chaque étape porte :
 *  - completed / completedAt : si l'étape est déjà réalisée
 *  - isCurrent    : true sur l'étape en cours (dernière étape franchie)
 *  - isNext       : true sur le prochain point immédiat (la même "next" que celle retournée)
 *  - isAfterNext  : true sur les étapes situées après le prochain point immédiat
 *
 * Ordre des étapes :
 *  1. pickup        → arrivée au pickup
 *  2. pickupDone    → pickup effectué (colis/commande récupéré)
 *  3. checkpoint[i]-reach   → arrivée au checkpoint i
 *  4. checkpoint[i]-deliver → livraison au checkpoint i
 *  5. destination   → arrivée à la destination finale
 */
function buildFullSteps(parsed) {
  const steps = []

  // --- 1. Arrivée au pickup ---
  steps.push({
    id: 'pickup',
    type: 'pickup',
    label: parsed.pickupLabel,
    address: parsed.pickupAddress,
    instructions: parsed.pickupInstructions || '',
    lat: parsed.pickupLat,
    lng: parsed.pickupLng,
    checkpointPosition: null,
    customerName: parsed.pickupCustomerName || parsed.customerName,
    customerPhoneNumber: parsed.pickupCustomerPhone || parsed.customerPhoneNumber,
    completed: !!parsed.pickupReached,
    completedAt: parsed.pickupReachedAt || null,
  })

  // --- 2. Pickup effectué ---
  steps.push({
    id: 'pickupDone',
    type: 'pickupDone',
    label: parsed.pickupLabel,
    address: parsed.pickupAddress,
    instructions: parsed.pickupInstructions || '',
    lat: parsed.pickupLat,
    lng: parsed.pickupLng,
    checkpointPosition: null,
    customerName: parsed.pickupCustomerName || parsed.customerName,
    customerPhoneNumber: parsed.pickupCustomerPhone || parsed.customerPhoneNumber,
    completed: !!parsed.pickedAt,
    completedAt: parsed.pickedAt || null,
  })

  // --- 3. Checkpoints (reach + deliver) ---
  ;(parsed.checkpoints || []).forEach((cp, i) => {
    const cpId = cp.id || `checkpoint-${i}`
    const common = {
      label: cp.label,
      address: cp.address,
      instructions: cp.instruction || '',
      lat: cp.lat,
      lng: cp.lng,
      checkpointPosition: i,
      customerName: cp.customerName,
      customerPhoneNumber: cp.customerPhoneNumber,
    }

    steps.push({
      id: `${cpId}-reach`,
      type: 'checkpoint_reach',
      ...common,
      completed: !!cp.reached,
      completedAt: cp.reachedAt || null,
    })

    steps.push({
      id: `${cpId}-deliver`,
      type: 'checkpoint_deliver',
      ...common,
      completed: !!cp.delivered,
      completedAt: cp.deliveredAt || null,
    })
  })

  // --- 4. Arrivée destination finale ---
  steps.push({
    id: 'destination',
    type: 'destination',
    label: parsed.destinationLabel,
    address: parsed.destinationAddress,
    instructions: parsed.destinationInstructions || '',
    lat: parsed.destinationLat,
    lng: parsed.destinationLng,
    checkpointPosition: null,
    customerName: parsed.destinationCustomerName || parsed.customerName,
    customerPhoneNumber: parsed.destinationCustomerPhone || parsed.customerPhoneNumber,
    completed: !!parsed.destinationReached,
    completedAt: parsed.destinationReachedAt || null,
  })

  // --- Marquage isCurrent / isNext / isAfterNext ---
  // Le "next" est la première étape non complétée (cohérent avec la logique `next` existante).
  const nextIndex = steps.findIndex(s => !s.completed)

  steps.forEach((step, i) => {
    step.isNext = nextIndex !== -1 && i === nextIndex
    step.isCurrent =
      nextIndex === -1
        ? i === steps.length - 1 // tout est fait → la dernière étape est "current"
        : i === nextIndex - 1    // l'étape juste avant next est la "current"
    step.isAfterNext = nextIndex !== -1 && i > nextIndex
  })

  return steps
}

/**
 * Parse legacy format (data already in correct structure)
 */
function parseLegacyFormat(data) {
  let parsed = {
    ...data,
    id: data?.id,
    status: data?.status,
    itemStatus: data?.itemStatus || data?.status,
    deliveryCode: data?.deliveryCode,

    orderDate: data?.orderDate,
    pickupLabel: data?.pickupLabel,
    pickupAddress: data?.pickupAddress,
    pickupLat: data?.pickupLat,
    pickupLng: data?.pickupLng,
    pickupAddressDescription: data?.pickupAddressDescription,
    pickupCity: data?.pickupCity,
    pickupReached: !!data?.pickupReached,
    pickupReachedAt: data?.pickupReachedAt,

    checkpoints: data?.checkpoints?.map((checkpoint, i) => {
      return {
        id: checkpoint.id,
        label: checkpoint.label,
        address: checkpoint.address,
        // description propagée depuis le champ API (indications de localisation)
        instruction: checkpoint.description || checkpoint.instruction || '',
        lat: checkpoint.lat,
        lng: checkpoint.lng,
        reached: !!checkpoint.reached,
        reachedAt: checkpoint.reachedAt,
        delivered: !!checkpoint.delivered,
        deliveredAt: checkpoint.deliveredAt,
        position: i,
        customerName: checkpoint.customerName,
        customerPhoneNumber: checkpoint.customerPhoneNumber,
        previousReached:
          i === 0 ? data?.pickupReached : data?.checkpoints[i - 1].reached,
        previousDelivered:
          i === 0 ? data?.pickupReached : data?.checkpoints[i - 1].delivered,
      }
    }),

    checkpointsReached: data?.checkpoints?.length
      ? data?.checkpoints[data?.checkpoints.length - 1].delivered
      : data?.pickupReached,

    destinationLabel: data?.destinationLabel,
    destinationAddress: data?.destinationAddress,
    destinationLat: data?.destinationLat,
    destinationLng: data?.destinationLng,
    destinationReached: data?.destinationReached,

    // Instructions de localisation (descriptions textuelles pour le rider)
    pickupInstructions: data?.pickupInstructions || '',
    destinationInstructions: data?.destinationInstructions || '',

    customerId: data?.customerId,
    customerName: data?.customerName,
    customerPhoneNumber: data?.customerPhoneNumber,
    restaurantPhone: data?.restaurantPhone,

    // Nested details for UI rendering
    items: data?.items || [],
    drinks: data?.drinks || [],
    restaurantName: data?.restaurantName,

    riderId: data?.riderId,
    riderName: data?.riderName,
    riderVehicle: data?.riderVehicle,
    riderPhone: data?.riderPhone,

    distanceKm: data?.distanceKm,
    estimatedDurationMin: data?.estimatedDurationMin,
    progressPercent: data?.progressPercent,

    packageType: data?.packageType,
    packageWeightKg: data?.packageWeightKg,
    packageDimensions: data?.packageDimensions,
    packagePhoto: data?.packagePhoto,
    packageDescription: data?.packageDescription,

    paymentMode: data?.paymentMode,
    paymentAmount: data?.paymentAmount,
    isCashOnDelivery: data?.isCashOnDelivery ?? (data?.paymentMode === 'cash' && !data?.paymentPaid),
    riderCommission: data?.riderCommission,
    paymentCurrency: data?.paymentCurrency,
    paymentPaid: data?.paymentPaid,

    assignedAt: data?.assignedAt,
    pickedAt: data?.pickedAt,
    destinationReachedAt: data?.destinationReachedAt,
    deliveredAt: data?.deliveredAt,

    type: data?.type || "package"
  }

   parsed.fullSteps = buildFullSteps(parsed)

  let next = null

  if (!parsed.pickupReached) {
    next = {
      id: 'pickup',
      type: 'pickup',
      label: parsed.pickupLabel,
      address: parsed.pickupAddress,
      instructions: parsed.pickupInstructions || '',
      lat: parsed.pickupLat,
      lng: parsed.pickupLng,
      checkpointPosition: null,
      customerName: parsed.pickupCustomerName || parsed.customerName,
      customerPhoneNumber: parsed.pickupCustomerPhone || parsed.customerPhoneNumber,
    }
  } else if (!parsed.pickedAt) {
    next = {
      id: 'pickupDone',
      type: 'pickupDone',
      label: parsed.pickupLabel,
      address: parsed.pickupAddress,
      instructions: parsed.pickupInstructions || '',
      lat: parsed.pickupLat,
      lng: parsed.pickupLng,
      checkpointPosition: null,
      customerName: parsed.pickupCustomerName || parsed.customerName,
      customerPhoneNumber: parsed.pickupCustomerPhone || parsed.customerPhoneNumber,
    }
  } else {
    // Trouver le premier checkpoint non entièrement traité (non livré)
    const pendingCheckpoint = parsed.checkpoints?.find(c => !c.delivered)

    if (pendingCheckpoint) {
      if (!pendingCheckpoint.reached) {
        // Le checkpoint n'est pas encore reached
        next = {
          id: pendingCheckpoint.id,
          label: pendingCheckpoint.label,
          address: pendingCheckpoint.address,
          instructions: pendingCheckpoint.instruction || '',
          lat: pendingCheckpoint.lat,
          lng: pendingCheckpoint.lng,
          type: 'checkpoint_reach',
          checkpointPosition: pendingCheckpoint.position,
          customerName: pendingCheckpoint.customerName,
          customerPhoneNumber: pendingCheckpoint.customerPhoneNumber,
        }
      } else {
        // Le checkpoint est reached mais pas encore livré
        next = {
          id: pendingCheckpoint.id,
          label: pendingCheckpoint.label,
          address: pendingCheckpoint.address,
          instructions: pendingCheckpoint.instruction || '',
          lat: pendingCheckpoint.lat,
          lng: pendingCheckpoint.lng,
          type: 'checkpoint_deliver',
          checkpointPosition: pendingCheckpoint.position,
          customerName: pendingCheckpoint.customerName,
          customerPhoneNumber: pendingCheckpoint.customerPhoneNumber,
        }
      }
    } else if (!parsed.destinationReached) {
      next = {
        id: 'destination',
        label: parsed.destinationLabel,
        address: parsed.destinationAddress,
        instructions: parsed.destinationInstructions || '',
        lat: parsed.destinationLat,
        lng: parsed.destinationLng,
        type: 'destination',
        checkpointPosition: null,
        customerName: parsed.destinationCustomerName || parsed.customerName,
        customerPhoneNumber: parsed.destinationCustomerPhone || parsed.customerPhoneNumber,
      }
    }
  }

  // Objet complet de la prochaine destination
  parsed.nextDestination = next

  // Rétrocompatibilité avec l'ancien format d'attributs individuels
  parsed.nextDestinationLabel = next?.label ?? null
  parsed.nextDestinationAddress = next?.address ?? null
  parsed.nextDestinationLat = next?.lat ?? null
  parsed.nextDestinationLng = next?.lng ?? null
  parsed.nextDestinationId = next?.id ?? null
  parsed.nextDestinationType = next?.type ?? null
  parsed.nextDestinationCheckpointPosition = next?.checkpointPosition ?? null

  return parsed
}

export default parseExternalDelivery